/**
 * PilotAcceptancePage — מסלול קבלת פיילוט (DIGEMP-PILOT-ACCEPTANCE-UX-FRONTEND-093).
 *
 * A read-only walkthrough of the real Digital Employee pilot flow: ticket queue → digital
 * employees → work runs → conversations → human decisions → verified outcomes → execution
 * path & safety. Each stage explains what it is in the product, derives a ready/blocked/
 * unknown state from evidence that already exists on the server (workforce overview, the
 * open-ticket list, recent conversations, connections config status, the crisis-mode flag),
 * and links straight into the real permission-gated screen where that stage is operated —
 * no screen is re-implemented here and no gate is duplicated, bypassed or weakened.
 *
 * Truthfulness rules (pinned by focused tests):
 *   - evidence that failed to load is "unknown" — never "ready" and never a fabricated zero;
 *   - a stage with no terminal runs in the window is "unknown": verification cannot be
 *     demonstrated before anything finished;
 *   - crisis mode is read (and claimed) only with agents_manage — without it the page says
 *     it cannot verify the state;
 *   - bounded reads (ticket sample, conversation sample, outcome scan) disclose their cap
 *     instead of presenting a sample as a complete count;
 *   - nothing raw is rendered: no JSON bodies, tool payloads, prompts or logs — every line
 *     is a human-readable projection already prepared by the server, and the data sources
 *     are listed in a collapsed footer.
 *
 * Beyond the seven stages the page renders the ACTION-001 write-cutover readiness
 * (DIGEMP-FINISH-PILOT-CUTOVER-UX-166) exactly as the backend snapshot reports it: one
 * overall plus the three switch gates, each with its server-reported ready/blocked/unknown
 * state, the server's own reason and basis, and bounded counts. It is additive — never
 * folded into the seven-stage roll-up — offers no switch or execution control, and an older
 * payload without the block reads "not exposed", never "ready".
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { agentsApi, connectionsApi, conversationsApi, ticketsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar, { STATUS_META } from "../components/EmployeeAvatar";
import { formatWaitDuration } from "./AttentionInboxPage";
import { conversationStatusLabel } from "../components/conversationStatusVocabulary";

// ─── Constants ───────────────────────────────────────────────────────────────

export const TICKET_SAMPLE_LIMIT = 200;
export const CONVERSATION_SAMPLE_LIMIT = 100;
const EMPLOYEE_ROWS_MAX = 4;
const ROW_MAX = 3;

export const STAGE_STATE = {
  LOADING: "loading",
  READY: "ready",
  BLOCKED: "blocked",
  UNKNOWN: "unknown",
};

const STATE_META = {
  [STAGE_STATE.LOADING]: { label: "בודק…", badge: "bg-gray-100 text-gray-500" },
  [STAGE_STATE.READY]: { label: "מוכן", badge: "bg-green-100 text-green-700" },
  [STAGE_STATE.BLOCKED]: { label: "חסום", badge: "bg-red-100 text-red-700" },
  [STAGE_STATE.UNKNOWN]: { label: "לא ידוע", badge: "bg-gray-200 text-gray-600" },
};

const CONVERSATION_ACTIVE = (status) => !["resolved", "closed"].includes(status);

// The verified_basis values the audited verification rule (employee_experience) produces.
export const VERIFICATION_BASIS_HE = {
  technician_resolution_outcome: "אימות טכנאי",
  approved_close_executed: "סגירת קריאה מאושרת",
};

// ─── Pure derivations (exported for focused tests) ───────────────────────────

function countPhrase(n, singular, plural) {
  return n === 1 ? `${n} ${singular}` : `${n} ${plural}`;
}

function loadingOrUnknown(input) {
  if (input.loading && !input.data) return STAGE_STATE.LOADING;
  if (input.error && !input.data) return STAGE_STATE.UNKNOWN;
  return null;
}

function baseStage(key, icon, title, about, cta, href) {
  return { key, icon, title, about, cta, href, state: STAGE_STATE.LOADING, reasons: [], evidence: [], links: [], detail: null };
}

function unresolvedStage(stage, input, unknownText) {
  const state = loadingOrUnknown(input);
  if (!state) return null;
  return {
    ...stage,
    state,
    evidence:
      state === STAGE_STATE.LOADING
        ? [{ text: "טוען ראיות…", muted: true }]
        : [{ text: unknownText, muted: true }],
  };
}

function waitText(ageSeconds) {
  if (ageSeconds == null) return "משך ההמתנה אינו ידוע";
  const formatted = formatWaitDuration(ageSeconds * 1000);
  return formatted ? `ממתין כבר ${formatted}` : "משך ההמתנה אינו ידוע";
}

function ticketTextOf(row) {
  if (row.ticket_number != null) {
    return row.ticket_title ? `קריאה #${row.ticket_number} — ${row.ticket_title}` : `קריאה #${row.ticket_number}`;
  }
  return row.ticket_id ? "קריאה משויכת" : "ללא קריאה משויכת";
}

export function buildTicketStage(input) {
  const stage = baseStage(
    "tickets", "📋", "תור הקריאות",
    "כאן מתחילה העבודה: קריאות שירות פתוחות שממתינות לטיפול טכנאי או לעובד דיגיטלי.",
    "פתח את תור הקריאות", "/tickets"
  );
  const pending = unresolvedStage(stage, input, "לא ניתן לטעון את תור הקריאות — מצב התור אינו ידוע.");
  if (pending) return pending;

  const list = Array.isArray(input.data) ? input.data : [];
  const evidence = [];
  if (list.length >= TICKET_SAMPLE_LIMIT) {
    evidence.push({ text: `לפחות ${list.length} קריאות פתוחות (נספרו ${TICKET_SAMPLE_LIMIT} הפתוחות הראשונות).` });
  } else if (list.length === 0) {
    evidence.push({ text: "אין קריאות פתוחות כרגע." });
  } else {
    evidence.push({ text: `${countPhrase(list.length, "קריאה פתוחה ממתינה", "קריאות פתוחות ממתינות")} לטיפול.` });
  }

  const links = [];
  const first = list[0];
  if (first?.id) {
    const number = first.ticket_number != null ? ` (#${first.ticket_number})` : "";
    links.push({ label: `פתח את הקריאה האחרונה בתור${number}`, to: `/tickets/${first.id}` });
  }
  return { ...stage, state: STAGE_STATE.READY, evidence, links };
}

export function buildEmployeeStage(input) {
  const stage = baseStage(
    "employees", "🤖", "העובדים הדיגיטליים",
    "מיהם העובדים שיבצעו את העבודה, ומה זמינותם ועומסם כעת.",
    "פתח את כוח העובדים", "/agents"
  );
  const pending = unresolvedStage(stage, input, "לא ניתן לטעון את מצב העובדים — מצבם אינו ידוע.");
  if (pending) return pending;

  const totals = input.data?.totals ?? {};
  const employees = Array.isArray(input.data?.employees) ? input.data.employees : [];
  const reasons = [];
  if ((totals.employees ?? 0) === 0) {
    reasons.push("לא הוגדר אף עובד דיגיטלי — אין מי שיבצע את העבודה.");
  } else if ((totals.disabled ?? 0) >= (totals.employees ?? 0)) {
    reasons.push("כל העובדים הדיגיטליים מושבתים — יש להפעיל לפחות עובד אחד לפני תחילת העבודה.");
  }

  const parts = [countPhrase(totals.employees ?? 0, "עובד דיגיטלי", "עובדים דיגיטליים")];
  parts.push(`${totals.available ?? 0} זמינים`);
  parts.push(`${totals.busy ?? 0} עסוקים`);
  if (totals.disabled) parts.push(`${totals.disabled} מושבתים`);
  if (totals.away) parts.push(`${totals.away} לא כאן`);
  if (totals.offline) parts.push(`${totals.offline} מנותקים`);

  const rows = employees.slice(0, EMPLOYEE_ROWS_MAX).map((employee) => ({
    id: employee.agent_id,
    name: employee.name,
    avatarKey: employee.avatar_key,
    avatarColor: employee.avatar_color,
    statusKey: employee.effective_status,
    statusLabel: STATUS_META[employee.effective_status]?.label ?? employee.effective_status,
    extra:
      (employee.awaiting_operator ?? 0) > 0
        ? `${employee.awaiting_operator} ממתינות להחלטה`
        : (employee.workload?.active_runs ?? 0) > 0
          ? `${employee.workload.active_runs} ריצות פעילות`
          : null,
    href: `/agents/${employee.agent_id}`,
  }));

  const evidence = [{ text: parts.join(" · ") }];
  if (employees.length > EMPLOYEE_ROWS_MAX) {
    evidence.push({ text: `מוצגים ${EMPLOYEE_ROWS_MAX} העובדים הראשונים לפי תשומת הלב.`, muted: true });
  }

  return {
    ...stage,
    state: reasons.length ? STAGE_STATE.BLOCKED : STAGE_STATE.READY,
    reasons,
    evidence,
    links: [{ label: "צור עובד חדש", to: "/employee-builder" }],
    detail: rows.length ? { kind: "employees", rows } : null,
  };
}

export function buildRunsStage(input) {
  const stage = baseStage(
    "runs", "⚡", "ריצות עבודה",
    "הפעלת העובד על קריאה ומעקב אחר ריצות פעילות — כולל המתנות בזמן אמת.",
    "פתח את ריצות העבודה", "/agent-runs"
  );
  const pending = unresolvedStage(stage, input, "לא ניתן לטעון את מצב הריצות — המצב אינו ידוע.");
  if (pending) return pending;

  const totals = input.data?.totals ?? {};
  const active = totals.active_runs ?? 0;
  const evidence = [
    { text: active > 0 ? `${countPhrase(active, "ריצה פעילה", "ריצות פעילות")} כעת.` : "אין ריצות פעילות כרגע." },
  ];

  const waits = [];
  if (totals.awaiting_operator) waits.push(countPhrase(totals.awaiting_operator, "ממתינה להחלטה אנושית", "ממתינות להחלטה אנושית"));
  if (totals.waiting_for_customer) waits.push(countPhrase(totals.waiting_for_customer, "ממתינה ללקוח", "ממתינות ללקוח"));
  if (totals.paused) waits.push(countPhrase(totals.paused, "מושהית", "מושהות"));
  if (waits.length) evidence.push({ text: waits.join(" · ") });

  const current = (input.data?.employees ?? []).find((employee) => employee.current_work?.run_id);
  const links = current
    ? [{ label: "פתח את הריצה הפעילה", to: `/agent-runs/${current.current_work.run_id}` }]
    : [];
  return { ...stage, state: STAGE_STATE.READY, evidence, links };
}

export function buildConversationsStage(input) {
  const stage = baseStage(
    "conversations", "💬", "שיחות ותקשורת",
    "התקשורת עם הלקוחות והעובדים — שאלות, תשובות ומסירה לטכנאי.",
    "פתח את השיחות", "/conversations"
  );
  const pending = unresolvedStage(stage, input, "לא ניתן לטעון את השיחות — מצב השיחות אינו ידוע.");
  if (pending) return pending;

  const list = Array.isArray(input.data) ? input.data : [];
  const capped = list.length >= CONVERSATION_SAMPLE_LIMIT;
  const evidence = [];
  if (list.length === 0) {
    evidence.push({ text: "אין שיחות שנפתחו עדיין." });
  } else {
    const parts = [capped ? `לפחות ${list.length} שיחות` : countPhrase(list.length, "שיחה", "שיחות")];
    const active = list.filter((conversation) => CONVERSATION_ACTIVE(conversation.status)).length;
    const handed = list.filter((conversation) => conversation.status === "handed_off").length;
    if (active) parts.push(countPhrase(active, "פעילה", "פעילות"));
    if (handed) parts.push(countPhrase(handed, "הועברה לאדם", "הועברו לאדם"));
    evidence.push({ text: `${parts.join(" · ")}.` });
    if (capped) evidence.push({ text: `נספרו ${CONVERSATION_SAMPLE_LIMIT} השיחות האחרונות בלבד.`, muted: true });
  }

  const sorted = [...list].sort((a, b) => (Date.parse(b.updated_at) || 0) - (Date.parse(a.updated_at) || 0));
  const rows = sorted.slice(0, ROW_MAX).map((conversation) => ({
    id: conversation.id,
    statusLabel: conversationStatusLabel(conversation.status),
    ticketText: conversation.ticket_id ? `קריאה #${String(conversation.ticket_id).slice(0, 8)}` : "ללא קריאה משויכת",
    href: `/conversations/${conversation.id}`,
  }));
  return { ...stage, state: STAGE_STATE.READY, evidence, detail: rows.length ? { kind: "conversations", rows } : null };
}

export function buildDecisionsStage(input) {
  const stage = baseStage(
    "decisions", "🛡️", "החלטות אנושיות",
    "כל מה שממתין לאדם: אישורים, סקירות טכנאי, כשלים והמתנות חריגות.",
    "פתח את תיבת ההחלטות", "/attention-inbox"
  );
  const pending = unresolvedStage(stage, input, "לא ניתן לטעון את ההחלטות הממתינות — מצב ההחלטות אינו ידוע.");
  if (pending) return pending;

  const totals = input.data?.totals ?? {};
  const attention = Array.isArray(input.data?.attention) ? input.data.attention : [];
  const awaiting = totals.awaiting_operator ?? 0;
  const evidence = [];
  if (awaiting === 0 && attention.length === 0) {
    evidence.push({ text: "אין החלטות הממתינות לאדם כרגע — כל התורים מטופלים." });
  } else {
    if (awaiting > 0) {
      evidence.push({ text: `${countPhrase(awaiting, "החלטה ממתינה", "החלטות ממתינות")} לאדם (אישורים וסקירות טכנאי).` });
    }
    if (attention.length > ROW_MAX) {
      evidence.push({ text: `מוצגים ${ROW_MAX} הפריטים הדחופים — הרשימה המלאה בתיבת ההחלטות.`, muted: true });
    }
  }

  const rows = attention.slice(0, ROW_MAX).map((item) => ({
    runId: item.run_id,
    label: item.label,
    agentName: item.agent_name,
    ticketText: ticketTextOf(item),
    waitText: waitText(item.age_seconds),
    href: `/agent-runs/${item.run_id}`,
  }));
  return { ...stage, state: STAGE_STATE.READY, evidence, detail: rows.length ? { kind: "rows", rows } : null };
}

export function buildOutcomesStage(input) {
  const stage = baseStage(
    "outcomes", "✅", "אימות תוצאות",
    "איך נקבעת הצלחה: ריצה נחשבת הצלחה מאומתת רק עם אימות אנושי מתועד — אחרת היא נספרת כהושלמה ללא אימות.",
    "פתח ריצות שהושלמו", "/agent-runs?status=completed"
  );
  const pending = unresolvedStage(stage, input, "לא ניתן לטעון את ראיות התוצאה — מצב האימות אינו ידוע.");
  if (pending) return pending;

  const totals = input.data?.totals ?? {};
  const scan = input.data?.outcome_evidence ?? {};
  const windowText = input.data?.window_days ? `בחלון ${input.data.window_days} הימים האחרונים` : "בחלון האחרון";

  if ((scan.scanned ?? 0) === 0) {
    return {
      ...stage,
      state: STAGE_STATE.UNKNOWN,
      evidence: [
        { text: `אין ריצות שהסתיימו ${windowText} — אין עדיין ראיות תוצאה.` },
        { text: "לא ניתן להציג אימות לפני שיושלמו ריצות בחלון.", muted: true },
      ],
    };
  }

  const evidence = [{
    text: `${windowText}: ${countPhrase(totals.verified_successes ?? 0, "הצלחה מאומתת", "הצלחות מאומתות")} · ${countPhrase(totals.unverified_completions ?? 0, "הושלמה ללא אימות", "הושלמו ללא אימות")} · ${countPhrase(totals.failures ?? 0, "כשל", "כשלים")} · ${countPhrase(totals.cancellations ?? 0, "ביטול", "ביטולים")}.`,
  }];
  if ((totals.unverified_completions ?? 0) > 0) {
    evidence.push({ text: "הושלמו ללא אימות אינן נספרות כהצלחה.", muted: true });
  }
  if (scan.truncated) {
    evidence.push({ text: `נסרקו ${scan.cap} הריצות האחרונות בלבד — ייתכנו תוצאות ותיקות יותר שלא נספרו.`, muted: true });
  }

  const rows = (input.data?.recent_verified_outcomes ?? []).slice(0, ROW_MAX).map((item) => ({
    runId: item.run_id,
    label: VERIFICATION_BASIS_HE[item.verified_basis] ?? "אימות מתועד",
    agentName: item.agent_name,
    ticketText: ticketTextOf(item),
    href: `/agent-runs/${item.run_id}`,
  }));
  return { ...stage, state: STAGE_STATE.READY, evidence, detail: rows.length ? { kind: "rows", rows } : null };
}

export function buildSafetyStage(connections, crisis, canManage) {
  const stage = baseStage(
    "safety", "🔌", "ביצוע, חיבורים ובטיחות",
    "מסלול הביצוע בפועל: מנוע ההרצה, החיבורים ללקוחות, מנוע הניתוח ומצב הבטיחות (מצב חירום).",
    "פתח את החיבורים", "/connections"
  );
  const pending = unresolvedStage(stage, connections, "לא ניתן לטעון את מצב החיבורים — המצב אינו ידוע.");
  if (pending) return pending;

  const data = connections.data ?? {};
  const reasons = [];
  const evidence = [];

  const openclaw = data.openclaw;
  if (openclaw) {
    const mode = openclaw.mode_label || openclaw.adapter || null;
    const configured = openclaw.configured === true;
    evidence.push({ text: `מנוע הביצוע: ${mode ?? "לא ידוע"} · ${configured ? "מוגדר" : "לא מוגדר"}.` });
    if (!configured) {
      reasons.push("מנוע הביצוע של העובדים אינו מוגדר לביצוע אמיתי — יש להגדיר Remote SSH או Local CLI לפני הדגמת ביצוע.");
    }
  } else {
    evidence.push({ text: "מנוע הביצוע: לא ידוע.", muted: true });
  }

  evidence.push({ text: `חיבור מרחוק ללקוחות (TeamViewer): ${data.teamviewer?.configured === true ? "מוגדר" : "לא מוגדר"}.` });
  evidence.push({ text: `דואר (Microsoft 365): ${data.ms365?.configured === true ? "מוגדר" : "לא מוגדר"}.` });

  const ai = data.ai_analysis;
  if (ai) {
    const configured = ai.configured === true;
    const label = configured ? [ai.provider, ai.model].filter(Boolean).join("/") : "";
    evidence.push({ text: `מנוע הניתוח של הריצות: ${label || "לא מוגדר"}.` });
    if (!configured) {
      reasons.push("מנוע הניתוח של הריצות אינו מוגדר — יש לאמת את מסלול הניתוח לפני הדגמת עבודה אמיתית.");
    }
  }

  if (!canManage) {
    evidence.push({ text: "מצב חירום: לא ניתן לאמת את המצב — נדרשת הרשאת ניהול (agents_manage).", muted: true });
  } else if (crisis?.loading && !crisis.data) {
    evidence.push({ text: "מצב חירום: טוען…", muted: true });
  } else if (crisis?.error && !crisis.data) {
    evidence.push({ text: "מצב חירום: לא ניתן לטעון את המצב — המצב אינו ידוע.", muted: true });
  } else if (crisis?.data?.active === true) {
    evidence.push({ text: "מצב חירום: פעיל." });
    reasons.push("מצב חירום פעיל — ייתכן שפעולות ואישורים של העובדים לא יבוצעו עד ליציאה ממצב חירום.");
  } else {
    evidence.push({ text: "מצב חירום: לא פעיל." });
  }

  let state = reasons.length ? STAGE_STATE.BLOCKED : STAGE_STATE.READY;
  if (state === STAGE_STATE.READY && canManage && crisis?.loading && !crisis.data) {
    state = STAGE_STATE.LOADING;
  }

  return {
    ...stage,
    state,
    reasons,
    evidence,
    links: canManage ? [{ label: "נהל מצב חירום", to: "/agents" }] : [],
  };
}

export function buildPilotStages({ workforce, tickets, conversations, connections, crisis, canManage }) {
  return [
    buildTicketStage(tickets),
    buildEmployeeStage(workforce),
    buildRunsStage(workforce),
    buildConversationsStage(conversations),
    buildDecisionsStage(workforce),
    buildOutcomesStage(workforce),
    buildSafetyStage(connections, crisis, canManage),
  ];
}

export function summarizeStages(stages) {
  const counts = { ready: 0, blocked: 0, unknown: 0, loading: 0 };
  for (const stage of stages) counts[stage.state] = (counts[stage.state] ?? 0) + 1;
  return counts;
}

// ─── Write-cutover readiness (DIGEMP-FINISH-PILOT-CUTOVER-UX-166) ────────────
// Read-only rendering of the ACTION-001 write-safety preflight
// (DIGEMP-FINISH-WRITE-CUTOVER-SURFACE-165) exposed on the pilot-acceptance snapshot.
// The three stable section keys are the server contract; the block never folds into the
// seven-stage roll-up and nothing here flips, toggles or executes anything.

export const CUTOVER_MISSING = "missing";

const CUTOVER_GATE_ORDER = [
  "strict_approval_token_binding",
  "legacy_unbound_refusal",
  "tool_entitlement_enforcement",
];

const CUTOVER_GATE_TITLES = {
  strict_approval_token_binding: "קישור אסימון האישור לבקשת הביצוע",
  legacy_unbound_refusal: "סירוב ריצה ללא אסימון אישור",
  tool_entitlement_enforcement: "אכיפת זכאות הכלים (Tool Registry)",
};

const CUTOVER_STATES = [STAGE_STATE.READY, STAGE_STATE.BLOCKED, STAGE_STATE.UNKNOWN];

function cutoverRollup(gates) {
  if (gates.some((gate) => gate.status === STAGE_STATE.BLOCKED)) return STAGE_STATE.BLOCKED;
  if (gates.some((gate) => gate.status === STAGE_STATE.UNKNOWN)) return STAGE_STATE.UNKNOWN;
  return STAGE_STATE.READY;
}

function addEvidenceCount(lines, value, label) {
  if (typeof value === "number" && Number.isFinite(value)) {
    lines.push({ text: `${label}: ${value}.` });
  }
}

function buildCutoverEvidence(key, evidence) {
  if (!evidence || typeof evidence !== "object") return [];
  const lines = [];
  if (key === "strict_approval_token_binding") {
    addEvidenceCount(lines, evidence.live_tokens, "אסימונים פעילים שנבדקו");
    addEvidenceCount(lines, evidence.refusable_after_flip, "אסימונים שההחלפה תסרב");
    addEvidenceCount(lines, evidence.no_request_hash, "אסימונים ללא קישור לבקשת הביצוע");
    addEvidenceCount(lines, evidence.unbound_provider_on_mail_action, "אסימוני דוא״ל ללא ספק מזוהה");
  } else if (key === "legacy_unbound_refusal") {
    addEvidenceCount(lines, evidence.runs_granted_approval_without_token, "ריצות עם אישור שניתן וללא רשומת אסימון");
  } else if (key === "tool_entitlement_enforcement") {
    addEvidenceCount(lines, evidence.enabled_agents, "עובדים פעילים שנבדקו");
    addEvidenceCount(lines, evidence.agents_blocked_after_flip, "עובדים שיאבדו פעולות כתיבה אחרי ההחלפה");
    const blocked = Array.isArray(evidence.blocked) ? evidence.blocked : [];
    for (const row of blocked.slice(0, ROW_MAX)) {
      if (!row || typeof row !== "object" || typeof row.agent_name !== "string") continue;
      const tier = row.permission_tier ? ` (${row.permission_tier})` : "";
      const actions = Array.isArray(row.blocked_write_actions) && row.blocked_write_actions.length
        ? ` — פעולות שייחסמו: ${row.blocked_write_actions.join(" · ")}`
        : "";
      lines.push({ text: `${row.agent_name}${tier}${actions}` });
    }
    if (blocked.length > ROW_MAX) {
      lines.push({ text: `מוצגים ${ROW_MAX} העובדים הראשונים — בסך הכול ${blocked.length}.`, muted: true });
    }
  }
  return lines;
}

function buildCutoverGate(key, section) {
  const title = CUTOVER_GATE_TITLES[key];
  if (!section || typeof section !== "object") {
    return {
      key,
      title,
      switchName: null,
      claim: null,
      engaged: null,
      status: STAGE_STATE.UNKNOWN,
      reason: "השרת לא כלל את מצב השער הזה — המצב אינו ידוע.",
      basis: null,
      evidence: [],
    };
  }
  return {
    key,
    title,
    switchName: typeof section.switch === "string" ? section.switch : null,
    claim: typeof section.claim === "string" ? section.claim : null,
    engaged: typeof section.engaged === "boolean" ? section.engaged : null,
    status: CUTOVER_STATES.includes(section.status) ? section.status : STAGE_STATE.UNKNOWN,
    reason: typeof section.reason === "string" ? section.reason : "השרת לא כלל נימוק לשער הזה.",
    basis: typeof section.basis === "string" ? section.basis : null,
    evidence: buildCutoverEvidence(key, section.evidence),
  };
}

export function buildWriteCutoverView(input) {
  if (input.loading && !input.data) return { state: STAGE_STATE.LOADING, note: "loading", gates: [] };
  if (!input.data) return { state: STAGE_STATE.UNKNOWN, note: "error", gates: [] };
  const block = input.data.write_cutover;
  if (!block || typeof block !== "object") return { state: CUTOVER_MISSING, note: "missing", gates: [] };
  const sections = block.sections && typeof block.sections === "object" ? block.sections : {};
  const gates = CUTOVER_GATE_ORDER.map((key) => buildCutoverGate(key, sections[key]));
  return {
    state: CUTOVER_STATES.includes(block.overall) ? block.overall : cutoverRollup(gates),
    note: null,
    engagedAll: block.engaged_all === true,
    gates,
  };
}

// ─── Building blocks ─────────────────────────────────────────────────────────

const SOURCE_NOTES = [
  { title: "תור הקריאות", note: `רשימת הקריאות הפתוחות (עד ${TICKET_SAMPLE_LIMIT} הראשונות).` },
  {
    title: "העובדים, הריצות, ההחלטות והתוצאות",
    note: "סיכום כוח העבודה הדיגיטלי: זמינות ועומס כפי שהם כעת, המתנות פתוחות, והצלחות/כשלים מאומתים בחלון 7 ימים.",
  },
  { title: "שיחות", note: `רשימת השיחות האחרונות (עד ${CONVERSATION_SAMPLE_LIMIT}).` },
  { title: "ביצוע ובטיחות", note: "מצב הגדרות החיבורים (ללא סודות) ומצב מצב חירום (בהרשאת ניהול בלבד)." },
  {
    title: "מוכנות למעבר לכתיבה (ACTION-001)",
    note: "בלוק write_cutover מתוך GET /api/agents/pilot-acceptance — תוצאת הבדיקה המוקדמת של שלושת מתגי בטיחות הכתיבה, כפי שהשרת מחזיר אותה (קריאה בלבד, ללא סודות).",
  },
];

function StateBadge({ state }) {
  const meta = STATE_META[state] ?? STATE_META.unknown;
  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${meta.badge}`}>
      מצב: {meta.label}
    </span>
  );
}

function StageDetail({ detail }) {
  if (detail.kind === "employees") {
    return (
      <ul className="space-y-1.5">
        {detail.rows.map((row) => (
          <li key={row.id} className="flex items-center gap-2 text-sm text-gray-700 flex-wrap">
            <EmployeeAvatar
              name={row.name}
              avatarKey={row.avatarKey}
              avatarColor={row.avatarColor}
              status={row.statusKey}
              size="sm"
              showStatus
            />
            <span className="font-medium">{row.name}</span>
            {row.statusLabel && <span className="text-xs text-gray-500">{row.statusLabel}</span>}
            {row.extra && <span className="text-xs text-amber-700">{row.extra}</span>}
            <Link to={row.href} aria-label={`פתח את העמוד של ${row.name}`} className="text-xs text-blue-700 hover:underline mr-auto">
              פתח
            </Link>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className="space-y-1.5">
      {detail.rows.map((row) => (
        <li key={row.runId ?? row.id} className="flex items-start justify-between gap-2">
          <div className="min-w-0 text-sm text-gray-700">
            {detail.kind === "conversations" ? (
              <div>
                <span className="text-gray-800">{row.statusLabel}</span>
                <span className="text-xs text-gray-500"> · {row.ticketText}</span>
              </div>
            ) : (
              <>
                <div>
                  <span className="text-gray-800">{row.label}</span>
                  <span className="text-xs text-gray-500"> · {row.agentName}</span>
                </div>
                <div className="text-xs text-gray-500 truncate">{row.ticketText}</div>
                <div className="text-xs text-gray-400">{row.waitText}</div>
              </>
            )}
          </div>
          <Link
            to={row.href}
            aria-label={
              detail.kind === "conversations"
                ? `פתח את השיחה — ${row.statusLabel} — ${row.ticketText}`
                : `פתח את הריצה — ${row.label} — ${row.agentName}`
            }
            className="text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-2.5 py-1 whitespace-nowrap shrink-0"
          >
            פתח
          </Link>
        </li>
      ))}
    </ul>
  );
}

function StageCard({ stage, index, total }) {
  return (
    <section
      aria-label={`שלב ${index} מתוך ${total}: ${stage.title}`}
      className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <span
            aria-hidden="true"
            className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-sm font-semibold shrink-0"
          >
            {index}
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-800 flex items-center gap-1.5">
              <span aria-hidden="true">{stage.icon}</span>
              {stage.title}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{stage.about}</p>
          </div>
        </div>
        <StateBadge state={stage.state} />
      </div>

      {stage.reasons.length > 0 && (
        <ul className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5 space-y-1">
          {stage.reasons.map((reason, i) => (
            <li key={i}>{reason}</li>
          ))}
        </ul>
      )}

      {stage.evidence.length > 0 && (
        <ul className="text-sm space-y-1">
          {stage.evidence.map((line, i) => (
            <li key={i} className={line.muted ? "text-gray-400 italic" : "text-gray-700"}>
              {line.text}
            </li>
          ))}
        </ul>
      )}

      {stage.detail && <StageDetail detail={stage.detail} />}

      <div className="flex items-center gap-3 flex-wrap">
        <Link to={stage.href} className="text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5">
          {stage.cta}
        </Link>
        {stage.links.map((link) => (
          <Link key={`${link.to}-${link.label}`} to={link.to} className="text-sm text-blue-700 hover:underline">
            {link.label}
          </Link>
        ))}
      </div>
    </section>
  );
}

function SourcesDetails() {
  return (
    <details className="text-xs text-gray-500 bg-white rounded-xl border border-gray-200 p-3">
      <summary className="cursor-pointer text-gray-600">מקורות הראיות בעמוד זה</summary>
      <ul className="mt-2 space-y-1">
        {SOURCE_NOTES.map((source) => (
          <li key={source.title}>
            <span className="font-medium text-gray-600">{source.title}:</span> {source.note}
          </li>
        ))}
      </ul>
    </details>
  );
}

// ─── Write-cutover building blocks ───────────────────────────────────────────

const CUTOVER_STATE_META = {
  [STAGE_STATE.LOADING]: { label: "בודק…", badge: "bg-gray-100 text-gray-500" },
  [STAGE_STATE.READY]: { label: "מוכן", badge: "bg-green-100 text-green-700" },
  [STAGE_STATE.BLOCKED]: { label: "חסום", badge: "bg-red-100 text-red-700" },
  [STAGE_STATE.UNKNOWN]: { label: "לא ידוע", badge: "bg-gray-200 text-gray-600" },
  [CUTOVER_MISSING]: { label: "לא נחשף", badge: "bg-white border border-dashed border-gray-400 text-gray-500" },
};

function CutoverBadge({ state }) {
  const meta = CUTOVER_STATE_META[state] ?? CUTOVER_STATE_META[STAGE_STATE.UNKNOWN];
  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${meta.badge}`}>
      {meta.label}
    </span>
  );
}

function CutoverGateRow({ gate }) {
  const reasonTone =
    gate.status === STAGE_STATE.BLOCKED
      ? "text-red-700 bg-red-50 border-red-200"
      : gate.status === STAGE_STATE.UNKNOWN
        ? "text-gray-600 bg-gray-50 border-gray-300"
        : "text-gray-600 bg-gray-50 border-gray-200";
  return (
    <li data-testid={`write-cutover-gate-${gate.key}`} className="rounded-lg border border-gray-200 p-3 space-y-2">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-800">{gate.title}</div>
          {gate.switchName && (
            <div dir="ltr" className="text-[11px] font-mono text-gray-400 text-left">{gate.switchName}</div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {gate.engaged !== null && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                gate.engaged ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-gray-100 text-gray-500"
              }`}
            >
              {gate.engaged ? "המתג כבר במצב המחמיר" : "המתג טרם הוחלף"}
            </span>
          )}
          <CutoverBadge state={gate.status} />
        </div>
      </div>

      {gate.claim && <p dir="ltr" className="text-xs text-gray-500 text-left">{gate.claim}</p>}

      <p dir="ltr" className={`text-sm text-left rounded-lg border p-2.5 ${reasonTone}`}>{gate.reason}</p>

      {gate.evidence.length > 0 && (
        <ul className="text-sm space-y-1">
          {gate.evidence.map((line, i) => (
            <li key={i} dir="auto" className={line.muted ? "text-xs text-gray-400 italic" : "text-gray-700"}>
              {line.text}
            </li>
          ))}
        </ul>
      )}

      {gate.basis && (
        <details className="text-xs text-gray-500">
          <summary className="cursor-pointer text-gray-500">על סמך מה נקבע המצב</summary>
          <p dir="ltr" className="mt-1 text-left">{gate.basis}</p>
        </details>
      )}
    </li>
  );
}

function WriteCutoverSection({ cutover }) {
  return (
    <section
      aria-label="מוכנות למעבר לכתיבה מלאה (ACTION-001)"
      data-testid="write-cutover"
      className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-800">מוכנות למעבר לכתיבה מלאה (ACTION-001)</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            בדיקת המוכנות של שלושת מתגי בטיחות הכתיבה לפני הפעלתם — קריאה בלבד. הבלוק אינו נכלל בספירת שבעת השלבים שלמעלה ואינו משנה שום מתג.
          </p>
        </div>
        <CutoverBadge state={cutover.state} />
      </div>

      {cutover.note === "loading" && (
        <p className="text-sm text-gray-400 italic">טוען את מצב מוכנות המעבר…</p>
      )}
      {cutover.note === "error" && (
        <p className="text-sm text-gray-600">לא ניתן לטעון את בדיקת מוכנות המעבר מהשרת — מצב המוכנות אינו ידוע.</p>
      )}
      {cutover.note === "missing" && (
        <p className="rounded-lg border border-dashed border-gray-400 bg-gray-50 p-3 text-sm text-gray-600">
          השרת הנוכחי אינו כולל את בלוק מוכנות המעבר (write_cutover) — אין לראות בכך מוכנות או חסימה; יש להריץ גרסת Backend הכוללת את הבלוק.
        </p>
      )}
      {cutover.note === null && (
        <>
          {cutover.state === STAGE_STATE.READY && (
            <p className="text-xs text-gray-400">
              {cutover.engagedAll
                ? "שלושת המתגים כבר במצב המחמיר."
                : "המעבר טרם בוצע — לפי מצב ההרצה הנוכחי, החלפת שלושת המתגים למצב המחמיר לא תסרב לפעילות קיימת."}
            </p>
          )}
          <ul className="space-y-2">
            {cutover.gates.map((gate) => (
              <CutoverGateRow key={gate.key} gate={gate} />
            ))}
          </ul>
          <p className="text-xs text-gray-400">החלפת המתגים עצמה מתבצעת בהגדרות השרת בלבד — מסך זה אינו כולל בקרה כלשהי.</p>
        </>
      )}
    </section>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function PilotAcceptancePage() {
  const { can, loading: permsLoading } = useAgentPermissions();
  const canView = can("agents_view");
  const canManage = can("agents_manage");

  const workforceQuery = useQuery({
    queryKey: ["pilot-acceptance-workforce"],
    queryFn: () => agentsApi.workforceOverview(),
    enabled: canView,
    staleTime: 15_000,
  });
  const ticketsQuery = useQuery({
    queryKey: ["pilot-acceptance-tickets"],
    queryFn: () => ticketsApi.list({ open_only: true, limit: TICKET_SAMPLE_LIMIT }),
    enabled: canView,
    staleTime: 15_000,
  });
  const conversationsQuery = useQuery({
    queryKey: ["pilot-acceptance-conversations"],
    queryFn: () => conversationsApi.list({ limit: CONVERSATION_SAMPLE_LIMIT }),
    enabled: canView,
    staleTime: 15_000,
  });
  const connectionsQuery = useQuery({
    queryKey: ["pilot-acceptance-connections"],
    queryFn: () => connectionsApi.health(),
    enabled: canView,
    staleTime: 30_000,
  });
  const crisisQuery = useQuery({
    queryKey: ["pilot-acceptance-crisis"],
    queryFn: () => agentsApi.getCrisisMode(),
    enabled: canView && canManage,
    staleTime: 30_000,
  });
  const cutoverQuery = useQuery({
    queryKey: ["pilot-acceptance-write-cutover"],
    queryFn: () => api.get("/api/agents/pilot-acceptance").then((r) => r.data),
    enabled: canView,
    staleTime: 30_000,
  });

  if (permsLoading) {
    return <div className="p-6 text-gray-500">טוען הרשאות…</div>;
  }

  if (!canView) {
    return <div className="p-6 text-gray-500">אין הרשאה לצפות במסלול קבלת הפיילוט</div>;
  }

  const stages = buildPilotStages({
    workforce: { data: workforceQuery.data, error: workforceQuery.isError, loading: workforceQuery.isLoading },
    tickets: { data: ticketsQuery.data, error: ticketsQuery.isError, loading: ticketsQuery.isLoading },
    conversations: { data: conversationsQuery.data, error: conversationsQuery.isError, loading: conversationsQuery.isLoading },
    connections: { data: connectionsQuery.data, error: connectionsQuery.isError, loading: connectionsQuery.isLoading },
    crisis: { data: crisisQuery.data, error: crisisQuery.isError, loading: crisisQuery.isLoading },
    canManage,
  });
  const summary = summarizeStages(stages);
  const total = stages.length;
  const lastUpdated = Math.max(
    0,
    workforceQuery.dataUpdatedAt,
    ticketsQuery.dataUpdatedAt,
    conversationsQuery.dataUpdatedAt,
    connectionsQuery.dataUpdatedAt,
    crisisQuery.dataUpdatedAt,
    cutoverQuery.dataUpdatedAt
  );
  const refreshAll = () => {
    [workforceQuery, ticketsQuery, conversationsQuery, connectionsQuery, crisisQuery, cutoverQuery].forEach((query) => query.refetch());
  };
  const crisisActive = canManage && crisisQuery.data?.active === true;

  return (
    <div className="w-full max-w-[1100px] mx-auto px-4 py-6 space-y-4" dir="rtl">
      <header className="space-y-1">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-gray-800">מסלול קבלת פיילוט</h1>
          <button
            type="button"
            onClick={refreshAll}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50"
          >
            רענן
          </button>
        </div>
        <p className="text-sm text-gray-500">
          מסע העבודה המלא של העובדים הדיגיטליים, שלב אחרי שלב: מה מוכן, מה חסום ומה לא ידוע — עם קישור ישיר לכל מסך אמיתי במוצר.
        </p>
        {lastUpdated > 0 && (
          <p className="text-xs text-gray-400">
            נטען לאחרונה: {new Date(lastUpdated).toLocaleTimeString("he-IL")}
          </p>
        )}
      </header>

      <div className="rounded-xl border border-gray-200 bg-white p-3 flex items-center gap-3 flex-wrap text-sm" role="status">
        <span className="font-medium text-gray-700">{total} שלבים במסלול:</span>
        <span className="text-green-700">{summary.ready} מוכנים</span>
        <span className="text-red-700">{summary.blocked} חסומים</span>
        <span className="text-gray-600">{summary.unknown} לא ידועים</span>
        {summary.loading > 0 && <span className="text-gray-400">בודק עוד {summary.loading}…</span>}
      </div>

      {crisisActive && (
        <div className="rounded-lg border border-red-300 bg-red-50 text-red-800 text-sm p-3">
          מצב חירום פעיל — ייתכן שפעולות ואישורים של העובדים לא יבוצעו עד ליציאה ממצב חירום.
        </div>
      )}

      {summary.blocked > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm p-3">
          יש שלבים חסומים במסלול — יש לפתור אותם לפני הליכה מלאה של הפיילוט.
        </div>
      )}

      {summary.unknown > 0 && summary.loading === 0 && (
        <div className="rounded-lg border border-gray-300 bg-gray-50 text-gray-700 text-sm p-3">
          חלק מהשלבים אינם ניתנים לאימות כרגע ומסומנים כ"לא ידוע" — הם לא יוצגו כמוכנים עד שיהיו ראיות.
        </div>
      )}

      <div className="space-y-3">
        {stages.map((stage, index) => (
          <StageCard key={stage.key} stage={stage} index={index + 1} total={total} />
        ))}
      </div>

      <WriteCutoverSection
        cutover={buildWriteCutoverView({ data: cutoverQuery.data, loading: cutoverQuery.isLoading })}
      />

      <SourcesDetails />
    </div>
  );
}
